import { ApiFacingGroup, ApiOptimizationConfigurationRequest } from './api';
import { ExcelAssortmentModel, NewShelfBlock, OptimalSpaceSegment } from './models';
import type { AisleGeometry, WarningsData } from '../state/job/job.types';
import { ILang } from './common';
import { Jobs } from '../state/jobs/jobs.types';
import { UserRole } from '../helpers/utils';

export type GetAvailableAislesParams = {
  storeNumber: string;
};

export type GetFileParams = {
  key: string;
  name: string;
  pogNumber: string;
};

export type GetProtoPogFileParams = {
  key: string;
  name: string;
  pogNumber: string;
  aisleCode: string;
  storeNumber: string;
};

export type GetOptimizationStatusParams = {
  jobId: string;
};

export type GetOptimizationConfigurationParams = {
  id: string;
  storeNumber: string;
};

export type GetAssortmentParams = {
  aisleCode: string;
  storeNumber: string;
  jobId?: string
};

type StoreAisleParams = {
  storeNumber: string;
  aisleCode: string;
};

export type GetDefaultPogMetricsParams = StoreAisleParams & {
  demand?: string;
  use_iss?: boolean;
  use_prom?: boolean;
};

export type GetDefaultProductMetricsParams = StoreAisleParams & {
  demand?: string;
  use_iss?: boolean;
  use_prom?: boolean;
};

export type LaunchOptimizationJobParams = ApiOptimizationConfigurationRequest;

export type GetMatchedPegboardConfigParams = {
  vendor: string;
  start_hole_x: number;
  start_hole_y: number;
  reqBody: AisleGeometry;
};

export type SaveDragAndDropEditsParams = {
  creations: ApiFacingGroup[];
  deletions: string[];
  moves: ApiFacingGroup[]; //TODO: hsould be edits i think.
};

export type getOptimizationJobParams = {
  storeNumber?: string;
  aisleId?: string;
  limit?: number;
};

export type getPogMetricsParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: string;
};

export type PushPogToStoreParams = {
  jobId: string;
  aisleCode: string;
  sourceStoreNum: string;
  targetStoreNum: string;
};

export type CopySharedPogParams = {
  jobId: string;
  aisleCode: string;
  sourceStoreNum: string;
  targetStoreNum: string;
};

export type GetProductMetricsParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: string;
};

export type PogSolverOutputParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: string;
};

export type AutoSavedPogSolverOutputParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: string;
};

export type getImagesParams = {
  images: Array<string>;
};

export type DownloadAssortmentExcelFileParam = {
  storeNumber: string;
  aisleName: string;
  fileName: string;
  reqBody: ExcelAssortmentModel;
};

export type DownloadOutputFileParam = {
  csvUrl: string | null;
  pdfUrl: string;
  fileName: string;
  alerts: WarningsData;
  lang: ILang;
  includeImages: boolean;
};

export type DownloadPEGxFileParam = {
  url: string;
  fileName: string;
  lang: ILang;
  alerts: WarningsData;
};

export type RequestErrorData = {
  error: string;
  api: string;
};

export enum DownloadFileTypes {
  XLSX = 'xlsx',
  XLSM = 'xlsm',
  PDF = 'pdf',
  CSV = 'csv',
}

export type DownloadMyDataFileParam = {
  name: string,
  store_num: string,
  lang: ILang,
  aisle_code: string,
  product_nums: Array<string>,
  starting_assortment: string,
  demand: string,
  labels: string,
  use_iss: boolean,
  use_prom: boolean,
  aisle_geometry: {
    blocks: NewShelfBlock[];
  },
  onError: () => void;
  onSuccess: () => void;
};

export type OptimalSpaceSegmentParams = {
  storeNumber: string;
  aisleCode: string;
  reqBody: OptimalSpaceSegment;
};

export type GetEditedJobParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: Array<string>;
};

export type SaveNewEditedPogParams = {
  storeNumber: string;
  aisleCode: string;
  jobId: string | undefined;
  reqBody: {};
};

export type SaveMyNotesParams = {
  store_num: string,
  aisle_code: string,
  job_id: string,
  parent_job_id: string,
  message: string
};

export type getFetchPogByStoreParams = {
  storeNumber: string
};

export type getFetchCombinedPogParams = {
  storeNumber: string
};

export type GetAllNotificationBannersParams = {
  params: boolean;
};

export type UpdateNotificationBannerParams = {
  notificationId: string;
  reqBody: {};
};

export type SaveNewNotificationBannerParams = {
  reqBody: {};
};

export type SaveUserPreferenceParams = {
  reqBody: {};
};

export type DeleteNotificationBannerParams = {
  notificationId: string;
};

export type AggregatedJobsParams = {
  storeNumber: string;
  aisleId: string;
  limit?: number;
  jobIds?: Array<string>;
};

export type AggregatedSolverOutputParams = {
  storeNumber: string;
  aisleId: string;
  jobs: Jobs;
};

export type AggregatedProductMetricsParams = {
  storeNumber: string;
  aisleCode: string;
  jobs: Jobs;
};

export type AggregatedPogMetricsParams = {
  storeNumber: string;
  aisleCode: string;
  jobs: Jobs;
};

export type ProductsInfoParams = {
  storeNumber: string;
  aisleCode: string;
};

export type GetAssortmentProductParams = {
  storeNumber: string;
  aisleCode: string;
  product_nums?: string[];
  jobId?: string
};

export type ProtoLinearParams = {
  aisleCode: string;
  orientation: string;
  label: string;
};

export type FlipPogParams = {
  storeNumber: string;
  aisleCode: string;
  job_id: string;
  is_edited: boolean;
};

export type GetAssortmentSearchParams = {
  storeId: string;
  searchString: string;
};

export type SetUserRoleParams = {
  username: string;
  new_roles: UserRole[];
};

export type SeedsParams = {
  storeNumber: string;
  aisleCode: string;
  reqBody: {
    aisle_geometry: AisleGeometry | null;
  };
};

export type UpdateSeedSharedParams = {
  store_num: string;
  job_id: string;
  shared: boolean;
};

export type SetAssortmentProductParams = {
  storeNumber: string;
  aisleCode: string;
  product_nums: string;
  jobId: string;
};

export type ApproveSeedParams = {
  store_num: string;
  job_id: string;
  corp_approved: boolean;
};

export type RemovePogParams = {
  job_id: string;
  type: string;
  aisle_code: string;
  store_num: string;
};

export type GetMerchGuideLinkParams = {
  storeNumber: string;
  aisleCode: string;
};

export type ChangeSharedPogNameParams = {
  store_num: string;
  job_id: string;
  shared_pog_name: string;
};

export type ChangePogNameParams = {
  store_num: string;
  job_id: string;
  pog_name: string;
};

export type ChangePrivateAndSharedPogNamesParams = {
  store_num: string;
  job_id: string;
  private_pog_name: string;
  shared_pog_name: string;
};

export type UpdateSeedOwnershipParams = {
  store_num: string;
  job_id: string;
  corp: boolean;
};
